import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  Palette,
  Type,
  Layout,
  Plus,
  Trash2,
  Check,
  Pencil,
  X,
  Sparkles,
  Layers,
  Activity,
  Copy,
  RefreshCw,
  Trophy,
  Globe,
  ExternalLink,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { api, regenerateTrackingKey } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import {
  useCustomDomain,
  useSetupCustomDomain,
  useVerifyCustomDomain,
  useUpdateRedirectMode,
  useRemoveCustomDomain,
} from '../hooks/useCustomDomain.ts';
import { SpaceAuthSettingsTab } from './SpaceAuthSettingsTab.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SpaceTheme {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
  headingFont?: string;
  baseFontSize?: string;
  borderRadius?: string;
  spacing?: string;
  // Extended loyalty platform fields
  preset?: string;
  surfaceColor?: string;
  surfaceBorderColor?: string;
  textColor?: string;
  textMutedColor?: string;
  fontFamilyHeading?: string;
  headingWeight?: string;
  headingStyle?: string;
  headingTransform?: string;
  letterSpacing?: string;
  shadowStyle?: string;
  glowEnabled?: string;
  glowColor?: string;
  skewEnabled?: string;
  patternBackground?: string;
}

interface NamedTheme extends SpaceTheme {
  id: string;
  name: string;
}

interface SpaceLayout {
  contentWidth?: 'narrow' | 'normal' | 'wide' | 'full';
  headerStyle?: 'sticky' | 'static' | 'minimal';
  navPosition?: 'top' | 'sidebar-left';
  footerStyle?: 'none' | 'minimal' | 'full';
}

interface CmsSpace {
  id: string;
  name: string;
  slug: string;
  type: string;
  settings: Record<string, unknown>;
  trackingWriteKey?: string | null;
}

// ─── Options ───────────────────────────────────────────────────────────────────

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'DM Sans', label: 'DM Sans' },
];

const FONT_SIZE_OPTIONS = [
  { value: '14px', label: '14px — Compact' },
  { value: '16px', label: '16px — Default' },
  { value: '18px', label: '18px — Large' },
];

const BORDER_RADIUS_OPTIONS = [
  { value: 'none', label: 'None — Sharp' },
  { value: 'sm', label: 'Small — Slightly rounded' },
  { value: 'md', label: 'Medium — Standard' },
  { value: 'lg', label: 'Large — More rounded' },
  { value: 'full', label: 'Full — Pill-shaped' },
];

const SPACING_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'spacious', label: 'Spacious' },
];

const PRESET_OPTIONS = [
  { value: '', label: '— No Preset —' },
  { value: 'clean', label: 'Clean — Blue, rounded, corporate' },
  { value: 'aggressive', label: 'Aggressive — Cyan, brutalist, italic uppercase' },
  { value: 'sport', label: 'Sport — Orange, brutalist, bold italic' },
  { value: 'custom', label: 'Custom — Manual configuration' },
];

const HEADING_WEIGHT_OPTIONS = [
  { value: 'semibold', label: 'Semibold (600)' },
  { value: 'bold', label: 'Bold (700)' },
  { value: 'extrabold', label: 'Extra Bold (800)' },
  { value: 'black', label: 'Black (900)' },
];

const HEADING_STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'italic', label: 'Italic' },
];

const HEADING_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'UPPERCASE' },
];

const LETTER_SPACING_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'wide', label: 'Wide (0.05em)' },
  { value: 'wider', label: 'Wider (0.1em)' },
];

const SHADOW_STYLE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'medium', label: 'Medium' },
  { value: 'brutalist', label: 'Brutalist — Hard offset' },
];

const PATTERN_BG_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'carbon', label: 'Carbon — Dot grid pattern' },
  { value: 'dots', label: 'Dots' },
  { value: 'grid', label: 'Grid' },
];

const THEME_PRESET_DEFAULTS: Record<string, Partial<SpaceTheme>> = {
  clean: {
    primaryColor: '#067ff9', secondaryColor: '#0f1923', accentColor: '#3b82f6',
    backgroundColor: '#0f1923', surfaceColor: '#1e293b', surfaceBorderColor: '#334155',
    textColor: '#f1f5f9', textMutedColor: '#94a3b8',
    fontFamily: 'Inter', fontFamilyHeading: 'Inter',
    headingWeight: 'bold', headingStyle: 'normal', headingTransform: 'none',
    letterSpacing: 'normal', borderRadius: 'lg', shadowStyle: 'subtle',
    glowEnabled: 'false', glowColor: '#067ff9', skewEnabled: 'false', patternBackground: 'none',
  },
  aggressive: {
    primaryColor: '#00f0ff', secondaryColor: '#0a111f', accentColor: '#00f0ff',
    backgroundColor: '#0a111f', surfaceColor: '#121d2f', surfaceBorderColor: '#1e293b',
    textColor: '#f1f5f9', textMutedColor: '#64748b',
    fontFamily: 'Inter', fontFamilyHeading: 'Inter',
    headingWeight: 'black', headingStyle: 'italic', headingTransform: 'uppercase',
    letterSpacing: 'wide', borderRadius: 'sm', shadowStyle: 'brutalist',
    glowEnabled: 'true', glowColor: '#00f0ff', skewEnabled: 'true', patternBackground: 'carbon',
  },
  sport: {
    primaryColor: '#ff9900', secondaryColor: '#0a1628', accentColor: '#ff9900',
    backgroundColor: '#0a1628', surfaceColor: '#111d33', surfaceBorderColor: '#1e3050',
    textColor: '#f1f5f9', textMutedColor: '#64748b',
    fontFamily: 'Inter', fontFamilyHeading: 'Inter',
    headingWeight: 'black', headingStyle: 'italic', headingTransform: 'uppercase',
    letterSpacing: 'wide', borderRadius: 'sm', shadowStyle: 'brutalist',
    glowEnabled: 'true', glowColor: '#ff9900', skewEnabled: 'true', patternBackground: 'carbon',
  },
};

const CONTENT_WIDTH_OPTIONS = [
  { value: 'narrow', label: 'Narrow — 672px' },
  { value: 'normal', label: 'Normal — 1024px (default)' },
  { value: 'wide', label: 'Wide — 1280px' },
  { value: 'full', label: 'Full width' },
];

const HEADER_STYLE_OPTIONS = [
  { value: 'sticky', label: 'Sticky — follows scroll (default)' },
  { value: 'static', label: 'Static — stays at top' },
  { value: 'minimal', label: 'Minimal — logo only, no navigation' },
];

const NAV_POSITION_OPTIONS = [
  { value: 'top', label: 'Top navigation bar (default)' },
  { value: 'sidebar-left', label: 'Sidebar — left panel' },
];

const FOOTER_STYLE_OPTIONS = [
  { value: 'none', label: 'None (default)' },
  { value: 'minimal', label: 'Minimal — brand name only' },
  { value: 'full', label: 'Full footer' },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function normalizeSettings(raw: Record<string, unknown>): {
  themes: NamedTheme[];
  activeThemeId: string;
  layout: SpaceLayout;
  loyaltyProgramId: string;
} {
  let themes: NamedTheme[];
  let activeThemeId: string;

  if (Array.isArray(raw.themes) && raw.themes.length > 0) {
    themes = raw.themes as NamedTheme[];
    activeThemeId = (raw.activeThemeId as string) ?? themes[0].id;
  } else if (raw.theme && typeof raw.theme === 'object') {
    const id = crypto.randomUUID();
    themes = [{ id, name: 'Default', ...(raw.theme as SpaceTheme) }];
    activeThemeId = id;
  } else {
    const id = crypto.randomUUID();
    themes = [{ id, name: 'Default' }];
    activeThemeId = id;
  }

  const layout = (raw.layout as SpaceLayout) ?? {};
  const loyaltyProgramId = (raw.loyaltyProgramId as string | undefined) ?? '';
  return { themes, activeThemeId, layout, loyaltyProgramId };
}

// ─── Shared field components ───────────────────────────────────────────────────

const inputCls =
  'w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white';
const labelCls = 'block text-xs font-medium text-zinc-500 mb-1.5';

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded-lg border border-zinc-200 cursor-pointer p-0.5 flex-shrink-0"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className={`${inputCls} font-mono`}
        />
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">— Default —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-zinc-100">
        <Icon size={15} className="text-zinc-500" />
        <h2 className="text-sm font-semibold text-zinc-800">{title}</h2>
      </div>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

// ─── ThemeCard ─────────────────────────────────────────────────────────────────

function ThemeCard({
  theme,
  isActive,
  isOnly,
  onEdit,
  onActivate,
  onDelete,
}: {
  theme: NamedTheme;
  isActive: boolean;
  isOnly: boolean;
  onEdit: () => void;
  onActivate: () => void;
  onDelete: () => void;
}) {
  const colors = [theme.primaryColor, theme.secondaryColor, theme.accentColor].filter(Boolean);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
        isActive ? 'border-zinc-800 bg-zinc-50' : 'border-zinc-200 bg-white'
      }`}
    >
      {/* Color preview */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {colors.length > 0 ? (
          colors.map((c, i) => (
            <div
              key={i}
              className="w-5 h-5 rounded-full border border-white shadow-sm ring-1 ring-zinc-200"
              style={{ backgroundColor: c }}
            />
          ))
        ) : (
          <div className="w-5 h-5 rounded-full bg-zinc-200 ring-1 ring-zinc-300" />
        )}
      </div>

      {/* Name + active badge */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm font-medium text-zinc-800 truncate">{theme.name}</span>
        {isActive && (
          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">
            <Check size={9} />
            Active
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isActive && (
          <button
            onClick={onActivate}
            className="text-xs text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded-md hover:bg-zinc-100 transition-colors"
          >
            Activate
          </button>
        )}
        <button
          onClick={onEdit}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-md hover:bg-zinc-100 transition-colors"
          title="Edit theme"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={onDelete}
          disabled={isOnly}
          className="p-1.5 text-zinc-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={isOnly ? 'Cannot delete the only theme' : 'Delete theme'}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── ThemeEditor ───────────────────────────────────────────────────────────────

function ThemeEditor({
  theme,
  onChange,
  onClose,
}: {
  theme: NamedTheme;
  onChange: (updated: NamedTheme) => void;
  onClose: () => void;
}) {
  function setField(key: keyof SpaceTheme, value: string) {
    onChange({ ...theme, [key]: value });
  }

  function applyPreset(presetKey: string) {
    const defaults = THEME_PRESET_DEFAULTS[presetKey];
    if (defaults) {
      onChange({ ...theme, ...defaults, preset: presetKey });
    } else {
      onChange({ ...theme, preset: presetKey || undefined });
    }
  }

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
      {/* Editor header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-100 bg-zinc-50">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-zinc-400 font-medium">Editing</span>
          <input
            type="text"
            value={theme.name}
            onChange={(e) => onChange({ ...theme, name: e.target.value })}
            className="text-sm font-semibold text-zinc-800 bg-transparent border-0 border-b border-dashed border-zinc-300 focus:outline-none focus:border-zinc-600 px-0 py-0 w-40"
          />
        </div>
        <button
          onClick={onClose}
          className="p-1 text-zinc-400 hover:text-zinc-700 rounded hover:bg-zinc-200 transition-colors"
          title="Close editor"
        >
          <X size={14} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Preset selector */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Theme Preset</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Preset</label>
              <select
                value={theme.preset ?? ''}
                onChange={(e) => applyPreset(e.target.value)}
                className={inputCls}
              >
                {PRESET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {theme.preset && theme.preset !== 'custom' && (
                <p className="text-[10px] text-zinc-400 mt-1">
                  Selecting a preset pre-fills all fields below. You can override individual values.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Colors */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Palette size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Brand Colors</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ColorField label="Primary Color" value={theme.primaryColor ?? ''} onChange={(v) => setField('primaryColor', v)} />
            <ColorField label="Secondary Color" value={theme.secondaryColor ?? ''} onChange={(v) => setField('secondaryColor', v)} />
            <ColorField label="Accent Color" value={theme.accentColor ?? ''} onChange={(v) => setField('accentColor', v)} />
            <ColorField label="Background Color" value={theme.backgroundColor ?? ''} onChange={(v) => setField('backgroundColor', v)} />
          </div>
        </div>

        {/* Surface Colors */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layers size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Surface & Text</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ColorField label="Surface Color (cards)" value={theme.surfaceColor ?? ''} onChange={(v) => setField('surfaceColor', v)} />
            <ColorField label="Surface Border" value={theme.surfaceBorderColor ?? ''} onChange={(v) => setField('surfaceBorderColor', v)} />
            <ColorField label="Text Color" value={theme.textColor ?? ''} onChange={(v) => setField('textColor', v)} />
            <ColorField label="Muted Text Color" value={theme.textMutedColor ?? ''} onChange={(v) => setField('textMutedColor', v)} />
          </div>
        </div>

        {/* Typography */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Type size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Typography</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label="Body Font" value={theme.fontFamily ?? ''} options={FONT_OPTIONS} onChange={(v) => setField('fontFamily', v)} />
            <SelectField label="Heading Font" value={theme.fontFamilyHeading ?? theme.headingFont ?? ''} options={FONT_OPTIONS} onChange={(v) => setField('fontFamilyHeading', v)} />
            <SelectField label="Base Font Size" value={theme.baseFontSize ?? ''} options={FONT_SIZE_OPTIONS} onChange={(v) => setField('baseFontSize', v)} />
            <SelectField label="Heading Weight" value={theme.headingWeight ?? ''} options={HEADING_WEIGHT_OPTIONS} onChange={(v) => setField('headingWeight', v)} />
            <SelectField label="Heading Style" value={theme.headingStyle ?? ''} options={HEADING_STYLE_OPTIONS} onChange={(v) => setField('headingStyle', v)} />
            <SelectField label="Heading Transform" value={theme.headingTransform ?? ''} options={HEADING_TRANSFORM_OPTIONS} onChange={(v) => setField('headingTransform', v)} />
            <SelectField label="Letter Spacing" value={theme.letterSpacing ?? ''} options={LETTER_SPACING_OPTIONS} onChange={(v) => setField('letterSpacing', v)} />
          </div>
        </div>

        {/* Layout */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layout size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Layout & Spacing</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label="Border Radius" value={theme.borderRadius ?? ''} options={BORDER_RADIUS_OPTIONS} onChange={(v) => setField('borderRadius', v)} />
            <SelectField label="Spacing Scale" value={theme.spacing ?? ''} options={SPACING_OPTIONS} onChange={(v) => setField('spacing', v)} />
            <SelectField label="Shadow Style" value={theme.shadowStyle ?? ''} options={SHADOW_STYLE_OPTIONS} onChange={(v) => setField('shadowStyle', v)} />
            <SelectField label="Background Pattern" value={theme.patternBackground ?? ''} options={PATTERN_BG_OPTIONS} onChange={(v) => setField('patternBackground', v)} />
          </div>
        </div>

        {/* Effects */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={13} className="text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Effects</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ColorField label="Glow Color" value={theme.glowColor ?? ''} onChange={(v) => setField('glowColor', v)} />
          </div>
        </div>

        {/* Preview */}
        {(theme.primaryColor || theme.fontFamily) && (
          <div
            className="border rounded-xl p-4 relative overflow-hidden"
            style={{
              backgroundColor: theme.backgroundColor || '#0f1923',
              borderColor: theme.surfaceBorderColor || '#334155',
            }}
          >
            <p className="text-xs mb-3 font-medium uppercase tracking-wider" style={{ color: theme.textMutedColor || '#94a3b8' }}>Preview</p>
            <div className="flex items-center gap-3" style={{ fontFamily: theme.fontFamily ?? undefined }}>
              {theme.primaryColor && (
                <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: theme.primaryColor }} />
              )}
              {theme.secondaryColor && (
                <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: theme.secondaryColor }} />
              )}
              {theme.accentColor && (
                <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: theme.accentColor }} />
              )}
              <div>
                <p
                  className="text-base"
                  style={{
                    color: theme.textColor || '#f1f5f9',
                    fontWeight: theme.headingWeight === 'black' ? 900 : theme.headingWeight === 'extrabold' ? 800 : theme.headingWeight === 'bold' ? 700 : 600,
                    fontStyle: theme.headingStyle || 'normal',
                    textTransform: (theme.headingTransform as 'uppercase' | 'none') || 'none',
                    letterSpacing: theme.letterSpacing === 'wide' ? '0.05em' : theme.letterSpacing === 'wider' ? '0.1em' : undefined,
                  }}
                >
                  Heading Preview
                </p>
                <p className="text-xs mt-1" style={{ color: theme.textMutedColor || '#94a3b8' }}>
                  Body text preview — {theme.fontFamily || 'Default font'}
                </p>
              </div>
            </div>
            {/* Mini card preview */}
            <div
              className="mt-3 p-3 rounded-lg"
              style={{
                backgroundColor: theme.surfaceColor || '#1e293b',
                border: `1px solid ${theme.surfaceBorderColor || '#334155'}`,
                borderLeft: theme.preset === 'aggressive' || theme.preset === 'sport'
                  ? `4px solid ${theme.primaryColor || '#067ff9'}`
                  : undefined,
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: theme.textColor || '#f1f5f9' }}>Card component</span>
                <span className="text-xs font-bold" style={{ color: theme.primaryColor || '#067ff9' }}>+200 PTS</span>
              </div>
              <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.surfaceBorderColor || '#334155' }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: '75%',
                    backgroundColor: theme.primaryColor || '#067ff9',
                    boxShadow: theme.glowColor ? `0 0 10px ${theme.glowColor}40` : undefined,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

type SpaceTab = 'theme' | 'auth';

export function SpaceSettingsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as SpaceTab) || 'theme';
  const [activeTab, setActiveTab] = useState<SpaceTab>(initialTab);
  const qc = useQueryClient();
  const toast = useToast();

  function switchTab(tab: SpaceTab) {
    setActiveTab(tab);
    setSearchParams(tab === 'theme' ? {} : { tab });
  }

  const [themes, setThemes] = useState<NamedTheme[]>([]);
  const [activeThemeId, setActiveThemeId] = useState<string>('');
  const [editingThemeId, setEditingThemeId] = useState<string | null>(null);
  const [layout, setLayout] = useState<SpaceLayout>({});
  const [loyaltyProgramId, setLoyaltyProgramId] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [writeKeyJustCopied, setWriteKeyJustCopied] = useState(false);
  const [snippetJustCopied, setSnippetJustCopied] = useState(false);
  const writeKeyCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snippetCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (writeKeyCopyTimerRef.current) clearTimeout(writeKeyCopyTimerRef.current);
      if (snippetCopyTimerRef.current) clearTimeout(snippetCopyTimerRef.current);
    };
  }, []);

  const { data: space, isLoading } = useQuery<CmsSpace>({
    queryKey: ['cms-space', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}`).then((r) => r.data),
    enabled: !!spaceId,
  });

  useEffect(() => {
    if (space) {
      const { themes: t, activeThemeId: a, layout: l, loyaltyProgramId: lp } = normalizeSettings(space.settings ?? {});
      setThemes(t);
      setActiveThemeId(a);
      setLayout(l);
      setLoyaltyProgramId(lp);
      setEditingThemeId(null);
      setDirty(false);
    }
  }, [space]);

  const { data: loyaltyPrograms } = useQuery<{ id: string; name: string; slug: string; isActive: boolean }[]>({
    queryKey: ['loyalty-programs'],
    queryFn: () => api.get('/api/v1/loyalty/programs').then((r) => r.data.programs ?? r.data),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/cms/spaces/${spaceId}`, {
        settings: {
          ...(space?.settings ?? {}),
          theme: undefined,
          themes,
          activeThemeId,
          layout,
          loyaltyProgramId: loyaltyProgramId || undefined,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-space', spaceId] });
      toast.success('Settings saved');
      setDirty(false);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save settings')),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateTrackingKey(spaceId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-space', spaceId] });
      toast.success('Write key regenerated');
      setShowRegenerateConfirm(false);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to regenerate tracking key')),
  });

  const { data: trackingStats } = useQuery({
    queryKey: ['cms-space-tracking-stats', spaceId],
    queryFn: async () => {
      const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const [total, pageviews, identified] = await Promise.all([
        api.get(`/api/v1/signals?source=web&from=${from}&limit=1`).then(r => r.data.total as number),
        api.get(`/api/v1/signals?source=web&eventType=web.page_viewed&from=${from}&limit=1`).then(r => r.data.total as number),
        api.get(`/api/v1/signals?source=web&eventType=web.identified&from=${from}&limit=1`).then(r => r.data.total as number),
      ]);
      return { total, pageviews, identified };
    },
    enabled: !!spaceId && !!space?.trackingWriteKey,
  });

  // ─── Custom Domain ───────────────────────────────────────────────────────────
  const { data: customDomain, isLoading: cdLoading } = useCustomDomain(spaceId);
  const setupDomain = useSetupCustomDomain();
  const verifyDomain = useVerifyCustomDomain();
  const updateRedirect = useUpdateRedirectMode();
  const removeDomain = useRemoveCustomDomain();
  const [newDomainInput, setNewDomainInput] = useState('');
  const [showRemoveDomainConfirm, setShowRemoveDomainConfirm] = useState(false);

  function handleNewTheme() {
    const id = crypto.randomUUID();
    const newTheme: NamedTheme = { id, name: `Theme ${themes.length + 1}` };
    setThemes((prev) => [...prev, newTheme]);
    setEditingThemeId(id);
    setDirty(true);
  }

  function handleActivate(id: string) {
    setActiveThemeId(id);
    setDirty(true);
  }

  function handleDeleteConfirmed() {
    if (!confirmDelete) return;
    const next = themes.filter((t) => t.id !== confirmDelete);
    const nextActiveId = activeThemeId === confirmDelete ? next[0].id : activeThemeId;
    setThemes(next);
    setActiveThemeId(nextActiveId);
    if (editingThemeId === confirmDelete) setEditingThemeId(null);
    setDirty(true);
    setConfirmDelete(null);
  }

  function handleThemeChange(updated: NamedTheme) {
    setThemes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-40 bg-zinc-100 rounded animate-pulse" />
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={2} />
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <p className="text-sm">Space not found.</p>
        <Link to="/spaces" className="text-xs text-zinc-500 underline mt-2 inline-block">
          Back to spaces
        </Link>
      </div>
    );
  }

  const editingTheme = themes.find((t) => t.id === editingThemeId) ?? null;
  const themeToDelete = themes.find((t) => t.id === confirmDelete);

  const writeKey = space?.trackingWriteKey ?? null;
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const installSnippet = writeKey
    ? `<script src="${apiUrl}/public/track.js"></script>\n<script>MAP('init', '${writeKey}');</script>`
    : null;

  const TABS: { key: SpaceTab; label: string }[] = [
    { key: 'theme', label: 'Theme & Layout' },
    { key: 'auth', label: 'Login & Signup' },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Tab bar */}
      <div className="flex gap-1 border-b border-zinc-100">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => switchTab(tab.key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-md transition-colors ${
              activeTab === tab.key
                ? 'text-zinc-900 border-b-2 border-zinc-800 -mb-px'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Auth tab */}
      {activeTab === 'auth' && spaceId && (
        <SpaceAuthSettingsTab spaceId={spaceId} />
      )}

      {/* Theme & Layout tab */}
      {activeTab === 'theme' && (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Themes &amp; Layout</h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Manage named themes and the site layout for this space
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleNewTheme}>
            <Plus size={13} className="mr-1.5" />
            New Theme
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!dirty}
            size="sm"
          >
            <Save size={13} className="mr-1.5" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Theme list */}
      <div className="space-y-2">
        {themes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isActive={theme.id === activeThemeId}
            isOnly={themes.length === 1}
            onEdit={() => setEditingThemeId(editingThemeId === theme.id ? null : theme.id)}
            onActivate={() => handleActivate(theme.id)}
            onDelete={() => setConfirmDelete(theme.id)}
          />
        ))}
      </div>

      {/* Inline editor */}
      {editingTheme && (
        <ThemeEditor
          theme={editingTheme}
          onChange={handleThemeChange}
          onClose={() => setEditingThemeId(null)}
        />
      )}

      {/* Site Layout section */}
      <Section icon={Layout} title="Site Layout">
        <SelectField
          label="Content Width"
          value={layout.contentWidth ?? ''}
          options={CONTENT_WIDTH_OPTIONS}
          onChange={(v) => { setLayout((l) => ({ ...l, contentWidth: v as SpaceLayout['contentWidth'] })); setDirty(true); }}
        />
        <SelectField
          label="Header Style"
          value={layout.headerStyle ?? ''}
          options={HEADER_STYLE_OPTIONS}
          onChange={(v) => { setLayout((l) => ({ ...l, headerStyle: v as SpaceLayout['headerStyle'] })); setDirty(true); }}
        />
        <SelectField
          label="Navigation Position"
          value={layout.navPosition ?? ''}
          options={NAV_POSITION_OPTIONS}
          onChange={(v) => { setLayout((l) => ({ ...l, navPosition: v as SpaceLayout['navPosition'] })); setDirty(true); }}
        />
        <SelectField
          label="Footer Style"
          value={layout.footerStyle ?? ''}
          options={FOOTER_STYLE_OPTIONS}
          onChange={(v) => { setLayout((l) => ({ ...l, footerStyle: v as SpaceLayout['footerStyle'] })); setDirty(true); }}
        />
      </Section>

      {/* Loyalty section */}
      <Section icon={Trophy} title="Loyalty Program">
        <div className="col-span-2">
          <label className={labelCls}>Associated Program</label>
          <select
            value={loyaltyProgramId}
            onChange={(e) => { setLoyaltyProgramId(e.target.value); setDirty(true); }}
            className={inputCls}
          >
            <option value="">— None —</option>
            {(loyaltyPrograms ?? []).filter((p) => p.isActive).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            Customers who sign up or log in on this space will be automatically enrolled in this program.
          </p>
        </div>
      </Section>

      {/* Tracking section */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-zinc-100">
          <Activity size={15} className="text-zinc-500" />
          <h2 className="text-sm font-semibold text-zinc-800">Web Tracking</h2>
        </div>
        <div className="p-5 space-y-5">
          {/* Write Key */}
          <div>
            <label className={labelCls}>Write Key</label>
            {writeKey ? (
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={writeKey}
                  className={`${inputCls} font-mono bg-zinc-50`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(writeKey);
                    if (writeKeyCopyTimerRef.current) clearTimeout(writeKeyCopyTimerRef.current);
                    setWriteKeyJustCopied(true);
                    writeKeyCopyTimerRef.current = setTimeout(() => setWriteKeyJustCopied(false), 2000);
                  }}
                >
                  {writeKeyJustCopied ? <Check size={13} /> : <Copy size={13} />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRegenerateConfirm(true)}
                >
                  <RefreshCw size={13} className="mr-1.5" />
                  Regenerate
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm text-zinc-400 italic flex-1">No write key yet</p>
                <Button
                  size="sm"
                  onClick={() => regenerateMutation.mutate()}
                  loading={regenerateMutation.isPending}
                >
                  <Plus size={13} className="mr-1.5" />
                  Generate Key
                </Button>
              </div>
            )}
          </div>

          {/* Install Snippet */}
          {installSnippet && (
            <div>
              <label className={labelCls}>Install Snippet</label>
              <div className="relative">
                <pre className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-xs font-mono text-zinc-700 whitespace-pre overflow-x-auto">
                  {installSnippet}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(installSnippet);
                    if (snippetCopyTimerRef.current) clearTimeout(snippetCopyTimerRef.current);
                    setSnippetJustCopied(true);
                    snippetCopyTimerRef.current = setTimeout(() => setSnippetJustCopied(false), 2000);
                  }}
                  className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-zinc-700 bg-white rounded border border-zinc-200 hover:border-zinc-300 transition-colors"
                  title="Copy snippet"
                >
                  {snippetJustCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5">
                Paste this snippet in the &lt;head&gt; of your HTML pages.
              </p>
              {!apiUrl && (
                <p className="text-[11px] text-amber-600 mt-1">
                  ⚠ VITE_API_URL is not set — replace the script src with your full API URL before deploying.
                </p>
              )}
            </div>
          )}

          {/* 7-day stats */}
          <div>
            <p className={labelCls}>Last 7 days (tenant-wide)</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Page Views', value: trackingStats?.pageviews },
                { label: 'Identified', value: trackingStats?.identified },
                { label: 'Total Events', value: trackingStats?.total },
              ].map((stat) => (
                <div key={stat.label} className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-zinc-900">
                    {stat.value ?? '—'}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Domain section */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-zinc-100">
          <Globe size={15} className="text-zinc-500" />
          <h2 className="text-sm font-semibold text-zinc-800">Custom Domain</h2>
        </div>
        <div className="p-5 space-y-4">
          {cdLoading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : !customDomain ? (
            /* No domain configured */
            <div className="space-y-3">
              <p className="text-sm text-zinc-500">
                Add a custom domain to serve this space on your own domain (e.g. <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">portal.example.com</code>).
              </p>
              <div className="flex items-center gap-2">
                <input
                  value={newDomainInput}
                  onChange={(e) => setNewDomainInput(e.target.value)}
                  placeholder="portal.example.com"
                  className={`${inputCls} flex-1`}
                />
                <Button
                  size="sm"
                  disabled={!newDomainInput.trim()}
                  loading={setupDomain.isPending}
                  onClick={() => {
                    setupDomain.mutate(
                      { spaceId: spaceId!, domain: newDomainInput.trim() },
                      {
                        onSuccess: () => {
                          toast.success('Custom domain setup initiated');
                          setNewDomainInput('');
                        },
                        onError: (err) => toast.error(extractError(err, 'Failed to set up domain')),
                      },
                    );
                  }}
                >
                  <Plus size={13} className="mr-1.5" />
                  Set Up
                </Button>
              </div>
            </div>
          ) : customDomain.status === 'pending_validation' ? (
            /* Pending DNS validation */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Pending Validation
                </span>
                <span className="text-sm font-medium text-zinc-700">{customDomain.domain}</span>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-amber-800">
                  Create this DNS CNAME record to validate your certificate:
                </p>
                <div className="space-y-1.5">
                  <div>
                    <p className="text-[11px] text-amber-600">Name</p>
                    <code className="text-xs bg-white px-2 py-1 rounded border border-amber-200 block overflow-x-auto">
                      {customDomain.dnsValidationName}
                    </code>
                  </div>
                  <div>
                    <p className="text-[11px] text-amber-600">Value</p>
                    <code className="text-xs bg-white px-2 py-1 rounded border border-amber-200 block overflow-x-auto">
                      {customDomain.dnsValidationValue}
                    </code>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  loading={verifyDomain.isPending}
                  onClick={() =>
                    verifyDomain.mutate(spaceId!, {
                      onSuccess: (data) => toast.success(`Status: ${data.status}`),
                      onError: (err) => toast.error(extractError(err, 'Verification failed')),
                    })
                  }
                >
                  <RefreshCw size={13} className="mr-1.5" />
                  Check Status
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowRemoveDomainConfirm(true)}
                >
                  <Trash2 size={13} className="mr-1.5" />
                  Remove
                </Button>
              </div>
              <p className="text-[11px] text-zinc-400">Auto-checking every 30 seconds.</p>
            </div>
          ) : customDomain.status === 'creating_distribution' ? (
            /* Distribution deploying */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Loader2 size={10} className="animate-spin mr-1" />
                  Creating Distribution
                </span>
                <span className="text-sm font-medium text-zinc-700">{customDomain.domain}</span>
              </div>
              <p className="text-sm text-zinc-500">
                CloudFront distribution is deploying. This typically takes ~15 minutes.
              </p>
              <Button
                size="sm"
                loading={verifyDomain.isPending}
                onClick={() =>
                  verifyDomain.mutate(spaceId!, {
                    onSuccess: (data) => toast.success(`Status: ${data.status}`),
                    onError: (err) => toast.error(extractError(err, 'Check failed')),
                  })
                }
              >
                <RefreshCw size={13} className="mr-1.5" />
                Check Status
              </Button>
              <p className="text-[11px] text-zinc-400">Auto-checking every 30 seconds.</p>
            </div>
          ) : customDomain.status === 'active' ? (
            /* Active — show domain, CNAME, redirect mode */
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  Active
                </span>
                <a
                  href={`https://${customDomain.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-indigo-600 hover:underline flex items-center gap-1"
                >
                  {customDomain.domain}
                  <ExternalLink size={11} />
                </a>
              </div>

              {customDomain.cfDomainName && (
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-zinc-600 mb-1">
                    Point your domain CNAME to:
                  </p>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-zinc-200 block">
                    {customDomain.cfDomainName}
                  </code>
                </div>
              )}

              <div>
                <label className={labelCls}>Redirect Mode</label>
                <select
                  value={customDomain.redirectMode}
                  onChange={(e) =>
                    updateRedirect.mutate(
                      { spaceId: spaceId!, redirectMode: e.target.value },
                      {
                        onSuccess: () => toast.success('Redirect mode updated'),
                        onError: (err) => toast.error(extractError(err, 'Failed to update')),
                      },
                    )
                  }
                  className={inputCls}
                >
                  <option value="none">None — both domains active</option>
                  <option value="to_custom">Redirect default → custom domain</option>
                  <option value="to_default">Redirect custom → default domain</option>
                </select>
              </div>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowRemoveDomainConfirm(true)}
              >
                <Trash2 size={13} className="mr-1.5 text-red-500" />
                Remove Domain
              </Button>
            </div>
          ) : customDomain.status === 'failed' ? (
            /* Failed */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertCircle size={10} className="mr-1" />
                  Failed
                </span>
                <span className="text-sm font-medium text-zinc-700">{customDomain.domain}</span>
              </div>
              {customDomain.errorMessage && (
                <p className="text-sm text-red-600">{customDomain.errorMessage}</p>
              )}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  loading={verifyDomain.isPending}
                  onClick={() =>
                    verifyDomain.mutate(spaceId!, {
                      onSuccess: (data) => toast.success(`Status: ${data.status}`),
                      onError: (err) => toast.error(extractError(err, 'Retry failed')),
                    })
                  }
                >
                  Retry
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowRemoveDomainConfirm(true)}
                >
                  <Trash2 size={13} className="mr-1.5" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            /* deleting or unknown */
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Loader2 size={14} className="animate-spin" /> Removing domain...
            </div>
          )}
        </div>
      </div>

      </>
      )}

      {/* Confirm remove custom domain */}
      <ConfirmDialog
        open={showRemoveDomainConfirm}
        title="Remove custom domain"
        message={`Remove "${customDomain?.domain ?? ''}"? The CloudFront distribution and certificate will be deleted. This may take a few minutes.`}
        confirmLabel="Remove"
        variant="danger"
        loading={removeDomain.isPending}
        onConfirm={() =>
          removeDomain.mutate(spaceId!, {
            onSuccess: () => {
              toast.success('Domain removal started');
              setShowRemoveDomainConfirm(false);
            },
            onError: (err) => toast.error(extractError(err, 'Failed to remove domain')),
          })
        }
        onCancel={() => setShowRemoveDomainConfirm(false)}
      />

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete theme"
        message={
          themeToDelete
            ? `Delete "${themeToDelete.name}"? This cannot be undone.${themeToDelete.id === activeThemeId ? ' The first remaining theme will be activated automatically.' : ''}`
            : 'Delete this theme?'
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Confirm regenerate tracking key dialog */}
      <ConfirmDialog
        open={showRegenerateConfirm}
        title="Regenerate tracking key"
        message="The old write key will stop working immediately. Any sites using it will need to be updated."
        confirmLabel="Regenerate"
        variant="warning"
        loading={regenerateMutation.isPending}
        onConfirm={() => regenerateMutation.mutate()}
        onCancel={() => setShowRegenerateConfirm(false)}
      />
    </div>
  );
}
